import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  AccountListItemToggle,
  Button,
  CardWrapperWithHeader,
  Heading,
} from '@rabbit/elements/shared-components';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import { updateDocument } from '@rabbit/utils/api';
import { toast } from 'react-toastify';

type IWarrantyAdminProps = {
  /** Already checked in parent; used only for display hints */
  isSuperAdmin: boolean;
};

const ACCESS_DENY_PERSONAS = [
  'admin',
  'warrantor',
  'manufacturer',
  'repairer',
  'retailer',
] as const;

type AccessDenyPersona = (typeof ACCESS_DENY_PERSONAS)[number];
type AccessDenyPersonasState = Record<AccessDenyPersona, boolean>;

const EMPTY_ACCESS_DENY_PERSONAS: AccessDenyPersonasState = {
  admin: false,
  warrantor: false,
  manufacturer: false,
  repairer: false,
  retailer: false,
};

type TenantSettingsCardProps = {
  tenantDocId: string;
  canUseAdminTools: boolean;
  isSaving: boolean;
  onSave: () => void | Promise<void>;
  paymentOverdueAlert: boolean;
  onTogglePaymentOverdueAlert: () => void;
  paymentOverdueAlertEmailsText: string;
  onPaymentOverdueAlertEmailsTextChange: (next: string) => void;
  accessDeny: {
    all: boolean;
    personas: AccessDenyPersonasState;
    emailsText: string;
    onToggleAll: () => void;
    onTogglePersona: (persona: AccessDenyPersona) => void;
    onEmailsTextChange: (next: string) => void;
  };
};

const TenantSettingsCard = memo(function TenantSettingsCard({
  tenantDocId,
  canUseAdminTools,
  isSaving,
  onSave,
  paymentOverdueAlert,
  onTogglePaymentOverdueAlert,
  paymentOverdueAlertEmailsText,
  onPaymentOverdueAlertEmailsTextChange,
  accessDeny,
}: TenantSettingsCardProps) {
  const accessDenyItems = useMemo(
    () =>
      [
        { persona: 'admin' as const, title: 'Deny admin access' },
        { persona: 'warrantor' as const, title: 'Deny warrantor access' },
        { persona: 'manufacturer' as const, title: 'Deny manufacturer access' },
        { persona: 'repairer' as const, title: 'Deny repairer access' },
        { persona: 'retailer' as const, title: 'Deny retailer access' },
      ] satisfies Array<{ persona: AccessDenyPersona; title: string }>,
    []
  );

  return (
    <CardWrapperWithHeader
      title="Tenant settings"
      subtitle={`Active tenant: ${tenantDocId}`}
      noPadding
      headerRight={
        <div className="flex items-center gap-3">
          {!canUseAdminTools && (
            <span className="text-xs text-gray-600">Admin access required</span>
          )}
          <Button
            kind="primary"
            className="h-[34px] px-6"
            disabled={!canUseAdminTools || isSaving}
            onClick={onSave}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="divide-y divide-gray-200">
        {/* ---------------------------- BILLING SECTION ---------------------------- */}
        <div className="p-5">
          <div className="font-caudex text-base text-gray-900">Billing</div>
          <div className="mt-1 text-sm text-gray-600">
            Billing-related operational controls for this tenant.
          </div>

          <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
            <AccountListItemToggle
              title="Show Payment overdue banner"
              state={paymentOverdueAlert}
              onChange={onTogglePaymentOverdueAlert}
              disabled={!canUseAdminTools || isSaving}
            />
            <div className="border-t border-gray-200 px-5 py-3 text-sm text-gray-600">
              When enabled, Sage shows a small red banner across all pages
              indicating payment is overdue.
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
            <div className="px-5 py-3">
              <div className="font-caudex text-sm text-gray-900">
                Additional allowed emails
              </div>
              <div className="mt-1 text-sm text-gray-600">
                One email per line (or comma-separated). These emails will also
                see the banner when it’s enabled.
              </div>
            </div>
            <div className="border-t border-gray-200 px-5 py-3">
              <textarea
                className="w-full rounded-md border border-gray-300 p-3 font-nunito text-sm text-gray-900"
                rows={4}
                value={paymentOverdueAlertEmailsText}
                disabled={!canUseAdminTools || isSaving}
                onChange={(e) =>
                  onPaymentOverdueAlertEmailsTextChange(e.target.value)
                }
                placeholder="e.g.info@supagard.com"
              />
            </div>
          </div>
        </div>

        {/* ----------------------------- ACCESS CONTROL ---------------------------- */}
        <div className="p-5">
          <div className="font-caudex text-base text-gray-900">
            Access control
          </div>
          <div className="mt-1 text-sm text-gray-600">
            Deny platform access for specific personas or emails.
          </div>

          <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
            <AccountListItemToggle
              title="Deny all tenant access"
              state={accessDeny.all}
              onChange={accessDeny.onToggleAll}
              disabled={!canUseAdminTools || isSaving}
            />
            {accessDenyItems.map((item) => (
              <AccountListItemToggle
                key={item.persona}
                title={item.title}
                state={accessDeny.personas[item.persona]}
                onChange={() => accessDeny.onTogglePersona(item.persona)}
                disabled={!canUseAdminTools || isSaving}
              />
            ))}
            <div className="border-t border-gray-200 px-5 py-3 text-sm text-gray-600">
              Denied personas and emails are blocked from logging in or using the
              platform, even with valid credentials.
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
            <div className="px-5 py-3">
              <div className="font-caudex text-sm text-gray-900">
                Blocked emails
              </div>
              <div className="mt-1 text-sm text-gray-600">
                One email per line (or comma-separated). These accounts will be
                denied immediately on sign-in.
              </div>
            </div>
            <div className="border-t border-gray-200 px-5 py-3">
              <textarea
                className="w-full rounded-md border border-gray-300 p-3 font-nunito text-sm text-gray-900"
                rows={4}
                value={accessDeny.emailsText}
                disabled={!canUseAdminTools || isSaving}
                onChange={(e) => accessDeny.onEmailsTextChange(e.target.value)}
                placeholder="blocked@example.com"
              />
            </div>
          </div>
        </div>

        {/* --------------------------- PLACEHOLDER SECTION -------------------------- */}
        {/* <div className="px-5 py-3">
          <div className="font-caudex text-base text-gray-900">
            More settings
          </div>
          <div className="mt-1 text-sm text-gray-600">
            Add additional tenant-level internal settings here (feature flags,
            support overrides, etc.).
          </div>
        </div> */}
      </div>
    </CardWrapperWithHeader>
  );
});

export default function IWarrantyAdmin({ isSuperAdmin }: IWarrantyAdminProps) {
  const { t } = useTranslation();
  const { tenantInfo, activeTenantLink } = useContext(AppContext);

  const tenantLink = t('tenantLink');
  const tenantDocId = activeTenantLink ?? tenantLink;

  // Parent already gates this tab; treat that as source of truth so controls don't
  // end up disabled due to context import/path mismatches.
  const canUseAdminTools = isSuperAdmin;

  const [paymentOverdueAlert, setPaymentOverdueAlert] = useState<boolean>(
    Boolean(tenantInfo?.payment_overdue_alert)
  );
  const [paymentOverdueAlertEmailsText, setPaymentOverdueAlertEmailsText] =
    useState<string>('');
  const [accessDenyAll, setAccessDenyAll] = useState<boolean>(
    Boolean(tenantInfo?.access_deny_all)
  );
  const [accessDenyPersonas, setAccessDenyPersonas] =
    useState<AccessDenyPersonasState>({
      ...EMPTY_ACCESS_DENY_PERSONAS,
      admin: Boolean(tenantInfo?.access_deny_admin),
      repairer: Boolean(tenantInfo?.access_deny_repairer),
      retailer: Boolean(tenantInfo?.access_deny_retailer),
      manufacturer: Boolean(tenantInfo?.access_deny_manufacturer),
      warrantor: Boolean(tenantInfo?.access_deny_warrantor),
    });
  const [accessDenyEmailsText, setAccessDenyEmailsText] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setPaymentOverdueAlert(Boolean(tenantInfo?.payment_overdue_alert));
  }, [tenantInfo?.payment_overdue_alert]);

  useEffect(() => {
    const emails = tenantInfo?.payment_overdue_alert_emails ?? [];
    setPaymentOverdueAlertEmailsText(emails.join('\n'));
  }, [tenantInfo?.payment_overdue_alert_emails]);

  useEffect(() => {
    setAccessDenyAll(Boolean(tenantInfo?.access_deny_all));
    setAccessDenyPersonas({
      ...EMPTY_ACCESS_DENY_PERSONAS,
      admin: Boolean(tenantInfo?.access_deny_admin),
      repairer: Boolean(tenantInfo?.access_deny_repairer),
      retailer: Boolean(tenantInfo?.access_deny_retailer),
      manufacturer: Boolean(tenantInfo?.access_deny_manufacturer),
      warrantor: Boolean(tenantInfo?.access_deny_warrantor),
    });
    const emails = tenantInfo?.access_deny_emails ?? [];
    setAccessDenyEmailsText(emails.join('\n'));
  }, [
    tenantInfo?.access_deny_all,
    tenantInfo?.access_deny_admin,
    tenantInfo?.access_deny_repairer,
    tenantInfo?.access_deny_retailer,
    tenantInfo?.access_deny_manufacturer,
    tenantInfo?.access_deny_warrantor,
    tenantInfo?.access_deny_emails,
  ]);

  const parsedPaymentOverdueEmails = useMemo(() => {
    const raw = paymentOverdueAlertEmailsText ?? '';
    const parts = raw
      .split(/[\n,;]/g)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0);
    return Array.from(new Set(parts));
  }, [paymentOverdueAlertEmailsText]);

  const parsedAccessDenyEmails = useMemo(() => {
    const raw = accessDenyEmailsText ?? '';
    const parts = raw
      .split(/[\n,;]/g)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0);
    return Array.from(new Set(parts));
  }, [accessDenyEmailsText]);

  const handleSaveTenantSettings = useCallback(async () => {
    if (!canUseAdminTools) return;

    try {
      setIsSaving(true);
      await updateDocument(`tenant_public/${tenantDocId}`, {
        'info.payment_overdue_alert': paymentOverdueAlert,
        'info.payment_overdue_alert_emails': parsedPaymentOverdueEmails,
        'info.access_deny_all': accessDenyAll,
        'info.access_deny_admin': accessDenyPersonas.admin,
        'info.access_deny_repairer': accessDenyPersonas.repairer,
        'info.access_deny_retailer': accessDenyPersonas.retailer,
        'info.access_deny_manufacturer': accessDenyPersonas.manufacturer,
        'info.access_deny_warrantor': accessDenyPersonas.warrantor,
        'info.access_deny_emails': parsedAccessDenyEmails,
      });
      toast.success('Updated tenant settings.');
    } catch (err) {
      console.error(err);
      toast.error("Couldn't update tenant settings.");
    } finally {
      setIsSaving(false);
    }
  }, [
    canUseAdminTools,
    parsedPaymentOverdueEmails,
    paymentOverdueAlert,
    accessDenyAll,
    accessDenyPersonas,
    parsedAccessDenyEmails,
    tenantDocId,
  ]);

  const handleTogglePaymentOverdueAlert = useCallback(() => {
    setPaymentOverdueAlert((v) => !v);
  }, []);

  const handlePaymentOverdueAlertEmailsTextChange = useCallback((next: string) => {
    setPaymentOverdueAlertEmailsText(next);
  }, []);

  const handleToggleAccessDenyAll = useCallback(() => {
    setAccessDenyAll((v) => !v);
  }, []);
  const handleToggleAccessDenyPersona = useCallback((persona: AccessDenyPersona) => {
    setAccessDenyPersonas((prev) => ({ ...prev, [persona]: !prev[persona] }));
  }, []);
  const handleAccessDenyEmailsTextChange = useCallback((next: string) => {
    setAccessDenyEmailsText(next);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-2">
        <Heading kind="h2">iWarranty Admin</Heading>
        <p className="font-nunito text-lg text-gray-500">
          Internal-only tools and tenant controls.
        </p>
      </div>

      <div className="mx-auto mt-4 w-full lg:w-[860px]">
        <div className="flex flex-col gap-4">
          <TenantSettingsCard
            tenantDocId={tenantDocId}
            canUseAdminTools={canUseAdminTools}
            isSaving={isSaving}
            onSave={handleSaveTenantSettings}
            paymentOverdueAlert={paymentOverdueAlert}
            onTogglePaymentOverdueAlert={handleTogglePaymentOverdueAlert}
            paymentOverdueAlertEmailsText={paymentOverdueAlertEmailsText}
            onPaymentOverdueAlertEmailsTextChange={
              handlePaymentOverdueAlertEmailsTextChange
            }
            accessDeny={{
              all: accessDenyAll,
              personas: accessDenyPersonas,
              emailsText: accessDenyEmailsText,
              onToggleAll: handleToggleAccessDenyAll,
              onTogglePersona: handleToggleAccessDenyPersona,
              onEmailsTextChange: handleAccessDenyEmailsTextChange,
            }}
          />
        </div>
      </div>
    </div>
  );
}


